import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

/**
 * Resolves whether the current user's effective subscription tier grants API access
 * (the `api_access` entitlement). Used to gate the "API Documentation" links in the
 * app headers.
 *
 * Returns `allowed`:
 *   - `true`  → tier grants API access (or the entitlement is unset/unknown — fail-open)
 *   - `false` → tier explicitly denies API access (e.g. the free Hobbyist tier)
 *
 * Fail-open: while loading, on error, or when the entitlement is absent, `allowed` is
 * true so we never hide the link from a user who legitimately has access.
 */
export function useApiAccess() {
  const [allowed, setAllowed] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiService.getMyEntitlements();
        // Envelope: { data: { entitlements: { entitlements: { api_access } } } }
        const ent = resp?.data?.entitlements?.entitlements ?? resp?.entitlements?.entitlements;
        if (!cancelled && ent && ent.api_access === false) {
          setAllowed(false);
        }
      } catch {
        // Fail-open — leave allowed = true.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return allowed;
}

export default useApiAccess;
